import React, { useState } from 'react';
import { Field } from 'formik';
import { Grid, Typography, FormControl, FormLabel, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Box } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';

import FormikDropdownField from '../../../../shared/components/form/FormikDropdownField';
import SearchableStaffDropdown from '../../shared/dropdowns/SearchableStaffDropdown';
import GlobalButton from '../../../../shared/components/UI/GlobalButton';

const SIGNATURE_PERMISSION_MODES = [
  { id: 1, title: 'Users with Sign and Send Permission' },
  { id: 2, title: 'Specific Users' },
  { id: 3, title: 'Anyone Can Sign' }
];

const SignaturePermissionSection = ({ values, setFieldValue }) => {
  const { signaturePermissionMode, allowedSigners, specificSigners } = values;
  
  const [selectedAllowedStaff, setSelectedAllowedStaff] = useState(null);
  const [selectedSpecificStaff, setSelectedSpecificStaff] = useState(null);

  const handleModeChange = event => {
    const newMode = parseInt(event.target.value);
    setFieldValue('signaturePermissionMode', newMode);
    
    // Clear incompatible fields when mode changes
    if (newMode !== 1) {
      setFieldValue('allowedSigners', []);
      setSelectedAllowedStaff(null);
    }
    if (newMode !== 2) {
      setFieldValue('specificSigners', []);
      setSelectedSpecificStaff(null);
    }
  };

  const handleAddAllowedSigner = () => {
    if (!selectedAllowedStaff) {
      return;
    }

    // Check if staff already exists in allowed signers
    const existingIndex = allowedSigners.findIndex(
      signer => signer.value === selectedAllowedStaff.value
    );

    if (existingIndex < 0) {
      // Add new allowed signer
      setFieldValue('allowedSigners', [
        ...allowedSigners,
        {
          value: selectedAllowedStaff.value,
          label: selectedAllowedStaff.label
        }
      ]);
    }

    // Reset form
    setSelectedAllowedStaff(null);
  };

  const handleAddSpecificSigner = () => {
    if (!selectedSpecificStaff) {
      return;
    }

    // Check if staff already exists in specific signers
    const existingIndex = specificSigners.findIndex(
      signer => signer.value === selectedSpecificStaff.value
    );

    if (existingIndex < 0) {
      // Add new specific signer
      setFieldValue('specificSigners', [
        ...specificSigners,
        {
          value: selectedSpecificStaff.value,
          label: selectedSpecificStaff.label
        }
      ]);
    }

    // Reset form
    setSelectedSpecificStaff(null);
  };

  const handleRemoveAllowedSigner = idToRemove => {
    setFieldValue(
      'allowedSigners',
      allowedSigners.filter(signer => signer.value !== idToRemove)
    );
  };

  const handleRemoveSpecificSigner = idToRemove => {
    setFieldValue(
      'specificSigners',
      specificSigners.filter(signer => signer.value !== idToRemove)
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h3" gutterBottom>
          Signature Permissions
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Field
          name="signaturePermissionMode"
          component={FormikDropdownField}
          label="Restrict To"
          items={SIGNATURE_PERMISSION_MODES}
          itemType="default"
          onChange={handleModeChange}
          fullWidth
          required
          noEmptyValue
        />
      </Grid>

      {/* Mode 1: Show Allowed Signers (exception list) */}
      {signaturePermissionMode === 1 && (
        <>
          <Grid item xs={12}>
            <Typography variant="h3" gutterBottom>
              Allowed Signers (Users who can sign without "Sign and Send" permission)
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Add users who can sign contracts even without the "Sign and Send" permission.
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <SearchableStaffDropdown
                value={selectedAllowedStaff}
                label="Select User"
                onChange={setSelectedAllowedStaff}
                placeholder="Select a user"
                selectedOptions={allowedSigners.map(signer => ({
                  value: signer.value,
                  label: signer.label
                }))}
                isClearable
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="flex-end" height="100%">
              <GlobalButton
                variant="primary"
                handleClick={handleAddAllowedSigner}
                disabled={!selectedAllowedStaff}
                fullWidth
              >
                Add
              </GlobalButton>
            </Box>
          </Grid>

          {allowedSigners && allowedSigners.length > 0 && (
            <Grid item xs={12}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allowedSigners.map(signer => (
                    <TableRow key={signer.value}>
                      <TableCell>{signer.label}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAllowedSigner(signer.value)}
                          aria-label="Remove signer"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </>
      )}

      {/* Mode 2: Show Specific Signers (restricted list) */}
      {signaturePermissionMode === 2 && (
        <>
          <Grid item xs={12}>
            <Typography variant="h3" gutterBottom>
              Specific Signers (Only these users can sign contracts)
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Select specific users who are authorized to sign contracts using this template.
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <SearchableStaffDropdown
                label="Select User"
                value={selectedSpecificStaff}
                onChange={setSelectedSpecificStaff}
                placeholder="Select a user"
                selectedOptions={specificSigners.map(signer => ({
                  value: signer.value,
                  label: signer.label
                }))}
                isClearable
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="flex-end" height="100%">
              <GlobalButton
                variant="primary"
                handleClick={handleAddSpecificSigner}
                disabled={!selectedSpecificStaff}
                fullWidth
              >
                Add
              </GlobalButton>
            </Box>
          </Grid>

          {specificSigners && specificSigners.length > 0 && (
            <Grid item xs={12}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {specificSigners.map(signer => (
                    <TableRow key={signer.value}>
                      <TableCell>{signer.label}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveSpecificSigner(signer.value)}
                          aria-label="Remove signer"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </>
      )}

      {/* Mode 3: No additional UI needed */}
      {signaturePermissionMode === 3 && (
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            All users in your organization will be able to sign contracts using this template.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

SignaturePermissionSection.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired
};

export default SignaturePermissionSection;

